<template>
    <v-card>
        <v-container fluid class="pa-6">
          <!-- Header moderno y minimalista -->
            <div class="modern-header elevation-1">
                <div class="d-flex justify-space-between align-center">
                    <!-- Título con indicador visual -->
                    <div class="title-section">
                    <div class="d-flex align-center">
                        <div class="title-indicator mr-3"></div>
                        <div>
                        <h1 class="text-h4 font-weight-medium mb-0 primary--text">
                            Clientes por Campaña
                        </h1>
                        <p class="text-body-2 grey--text text--darken-1 mb-0 mt-1">
                            Gestiona y organiza tus clientes
                        </p>
                        </div>
                    </div>
                    </div>

                    <!-- Controles de acción -->
                    <div class="action-controls d-flex gap-3">
                    <!-- Toggle para modo arrastre -->
                    <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                        <v-btn
                            @click="toggleDragMode"
                            :class="{ 'drag-active': isDragMode }"
                            class="control-btn"
                            icon
                            large
                            v-bind="attrs"
                            v-on="on"
                        >
                            <v-icon color="#FFF">{{ isDragMode ? 'mdi-lock-open' : 'mdi-cursor-move' }}</v-icon>
                        </v-btn>
                        </template>
                        <span>{{ isDragMode ? 'Desactivar' : 'Activar' }} modo arrastre</span>
                    </v-tooltip>

                    <!-- Botón principal -->
                    <v-btn
                        v-if="!isDragMode"
                        @click="showCampaignModal = true"
                        color="primary"
                        class="primary-action-btn text-none"
                        large
                        depressed
                    >
                        <v-icon left>mdi-plus-circle</v-icon>
                        Nuevo Cliente
                    </v-btn>
                    </div>
                </div>
            </div>

            <!-- Contenedor de Filtros -->
            <v-card class="filter-section mb-6" elevation="1" v-if="!isDragMode">
                <v-card-text class="pa-4">
                    <div class="d-flex align-center mb-3">
                        <v-icon color="primary" class="mr-2">mdi-filter-variant</v-icon>
                        <h3 class="text-h6 font-weight-medium grey--text text--darken-2 mb-0">
                            Filtros
                        </h3>
                    </div>
                    <v-row style="align-items: end;">
                        <v-col cols="12" md="5">
                            <s-select-campaign-area
                                ref="campaignAreaFilter"
                                v-model="filter.CmaID"
                                label="Campaña"
                                typeArea="1"
                                full
                                @hook:mounted="onCampaignAreaMounted"
                            />
                        </v-col>
                        <v-col cols="12" md="5">
                            <s-select-definition
                                :def="1589"
                                label="Continente"
                                v-model="filter.CsoContinent"
                                clearable
                            />
                        </v-col>
                        <v-col cols="12" md="2">
                            <v-btn @click="applyFilters" color="primary" class="text-none">Buscar</v-btn>
                        </v-col>
                    </v-row>
                </v-card-text>
            </v-card>
      
          <!-- Secciones por CsoPriority -->
          <div class="priority-sections">
            <div 
              v-for="priority in priorities" 
              :key="priority.key"
              class="priority-section mb-8"
            >
              <!-- Header de sección con fondo -->
              <v-card class="section-card mb-4" elevation="1">
                <v-card-text class="pa-4">
                  <div class="section-header d-flex align-center">
                    <div 
                      :class="`priority-indicator priority-${priority.key}`"
                    ></div>
                    <h2 class="text-h6 font-weight-regular grey--text text--darken-1 mb-0 ml-3">
                      {{ priority.label }}
                    </h2>
                    <v-chip 
                      small 
                      :color="priority.color" 
                      text-color="white" 
                      class="ml-3"
                    >
                      {{ getCampaignsByPriority(priority.key).length }}
                    </v-chip>
                  </div>
                </v-card-text>
              </v-card>
      
              <!-- Container de cards con fondo -->
              <v-card class="cards-wrapper" elevation="0" :color="isDragMode ? '#f8f9fa' : 'transparent'">
                <v-card-text class="pa-4">
                  <draggable
                    :value="getCampaignsByPriority(priority.key)"
                    @input="updateCampaignsByPriority(priority.key, $event)"
                    group="Customers"
                    :disabled="!isDragMode"
                    @change="onDragChange($event, priority.key)"
                    class="cards-container"
                    :style="{ 'min-height': getCampaignsByPriority(priority.key).length !== 0 || (isDragMode) ? '100px' : 'auto' }"
                    :class="{ 'drag-enabled': isDragMode }"
                  >
                    <transition-group name="flip-list" tag="div" class="d-flex flex-wrap">
                      <v-card
                        v-for="customer in getCampaignsByPriority(priority.key)"
                        :key="`${customer.CsoID}-${customer.CsoPriority}`"
                        @click="openModal(customer)"
                        class="customer-card ma-2"
                        :class="{ 'draggable-card': isDragMode }"
                        elevation="3"
                        hover
                      >
                        <v-card-text class="pa-4">
                          <div class="d-flex justify-space-between align-start mb-3">
                            <div class="customer-info flex-grow-1" @click="openModal(customer)">
                              <h3 class="text-subtitle-1 font-weight-medium grey--text text--darken-3 mb-1">
                                {{ customer.CardName }}
                              </h3>
                              <p class="text-body-2 grey--text text--darken-1 mb-2">
                                {{ customer.CmaName }}
                              </p>
                            </div>
                            <div class="d-flex align-center gap-2">
                              <v-btn
                                @click.stop="editCampaign(customer)"
                                icon
                                small
                                color="grey"
                                class="edit-btn"
                                :disabled="isDragMode"
                              >
                                <v-icon small>mdi-pencil</v-icon>
                              </v-btn>
                              <v-btn
                                @click.stop="deleteCustomer(customer)"
                                icon
                                small
                                color="red"
                                class="delete-btn"
                                :disabled="isDragMode"
                              >
                                <v-icon small>mdi-delete</v-icon>
                              </v-btn>
                              <!-- <div 
                                class="customer-color-indicator"
                                :style="{ backgroundColor: customer.CsoColor || '#FF8C00' }"
                              ></div> -->
                            </div>
                          </div>
      
                          <div class="customer-details d-flex justify-space-between">
                            <div class="d-flex align-center">
                              <v-icon small color="grey darken-1" class="mr-2">mdi-earth</v-icon>
                              <span class="text-caption grey--text text--darken-1">{{ customer.CsoContinentName }}</span>
                            </div>
                            <div>
                              <v-chip x-small color="warning" class="white--text mr-2">{{ customer.CountPending }}</v-chip>
                              <v-chip x-small color="success" class="white--text">{{ customer.CountCompleted }}</v-chip>
                            </div>
                            
                            <!-- <div class="d-flex justify-space-between align-center">
                              <v-chip
                                x-small
                                :color="getPriorityColor(customer.CsoPriority)"
                                text-color="white"
                                class="text-caption"
                              >
                                {{ customer.CsoPriority }}
                              </v-chip>
                              <span class="text-caption grey--text">
                                Orden: {{ customer.CsoOrder }}
                              </span>
                            </div> -->
                          </div>
                        </v-card-text>
                      </v-card>
                    </transition-group>
                  </draggable>
      
                  <!-- Mensaje si no hay Customers -->
                  <div 
                    v-if="getCampaignsByPriority(priority.key).length === 0 && !isDragMode" 
                    class="empty-section text-center pa-8"
                  >
                    <v-icon large color="grey lighten-1" class="mb-2">mdi-inbox</v-icon>
                    <p class="text-body-2 grey--text">No hay clientes en esta prioridad</p>
                  </div>
                </v-card-text>
              </v-card>
            </div>
          </div>
      
          <!-- Modal para gestión de pedidos -->
          <v-dialog
            v-model="showModal"
            max-width="900px"
            persistent
            scrollable
          >
            <v-card v-if="selectedCampaign" class="modal-card">
              <v-card-title class="pa-6 pb-4">
                <div class="d-flex align-center justify-space-between w-100" style="width: 100%;">
                  <div>
                    <h2 class="text-h5 font-weight-regular grey--text text--darken-3">
                      {{ selectedCampaign.CardName }}
                    </h2>
                    <p class="text-body-1 grey--text text--darken-1 mb-0 mt-1">
                      {{ selectedCampaign.CmaName }}
                    </p>
                  </div>
                  <v-btn
                    @click="closeModal"
                    icon
                    large
                    color="grey"
                  >
                    <v-icon>mdi-close</v-icon>
                  </v-btn>
                </div>
              </v-card-title>
      
              <v-divider></v-divider>
      
              <v-card-text class="pa-0" style="max-height: 500px;">
                <v-tabs v-model="activeTab" class="tabs-container">
                    <v-tab class="text-none">
                        <v-icon left small>mdi-clock</v-icon>
                        Pendientes ({{ modalPendingOrders.length }})
                    </v-tab>
                    <v-tab class="text-none">
                        <v-icon left small>mdi-check-circle</v-icon>
                        Atendidos ({{ modalOrdersServed.length }})
                    </v-tab>
                </v-tabs>
      
                <v-tabs-items v-model="activeTab">    
                  <!-- Pedidos Pendientes -->
                  <v-tab-item>
                    <div class="pa-4">
                      <!-- Botón para agregar pedido -->
                      <div class="mb-4">
                        <v-btn
                          @click="showAddOrderForm = true"
                          v-if="!showAddOrderForm"
                          color="primary"
                          small
                          class="text-none"
                        >
                          <v-icon left small>mdi-plus</v-icon>
                          Agregar Pedido
                        </v-btn>
                      </div>
      
                      <!-- Formulario para agregar pedido -->
                      <v-card v-if="showAddOrderForm" class="mb-4" elevation="1">
                        <v-card-text class="pa-4">
                          <v-row>
                            <v-col cols="12">
                              <s-text v-if="editDataOrder" label="Descripción del Artículo" v-model="newOrder.SosArtName" readonly></s-text>
                              <s-article-search v-else v-model="OrderArticle" @selected="seletedData($event)"/>
                            </v-col>
                            <v-col cols="12" md="3">
                              <s-text
                                label="Codigo"
                                v-model="newOrder.SosArtCode"
                                disabled
                              ></s-text>
                            </v-col>
                            <v-col cols="12" md="3">
                              <s-select-definition
                                clearable
                                :def="1173"
                                label="Cultivo"
                                v-model="newOrder.TypeCultive"
                                @getDataInitial="getDataInitialCultivo($event)"
                              />
                            </v-col>
                            <v-col cols="12" md="3">
                              <s-select-variety
                                clearable
                                @change="changeVariety($event)"
                                :cultiveID="newOrder.TypeCultive"
                                label="Variedad"
                                full
                                v-model="newOrder.VrtID"
                              />
                            </v-col>
                            <v-col cols="12" md="3">
                              <s-select-definition
                                label="Calibre"
                                :dgr="34"
                                :def="1266"
                                v-model="newOrder.TypeCaliber"
                              >
                              </s-select-definition>
                            </v-col>
                            <v-col cols="12" md="3">
                              <s-text
                                v-model="newOrder.SosAmount"
                                label="Cantidad"
                                type="number"
                              ></s-text>
                            </v-col>
                            <v-col cols="12" md="2">
                              <s-text
                                v-model="newOrder.SosUnitOfMeasuarement"
                                label="Unidad"
                                disabled
                              ></s-text>
                            </v-col>
                            <v-col cols="12" md="2">
                              <s-text
                                v-model="newOrder.SosUnitPrice"
                                label="Precio unitario"
                                decimal
                                :maxDecimal="2"
                              ></s-text>
                            </v-col>
                            <v-col cols="12" md="2">
                              <s-select-definition
                                :def="1129"
                                label="Tipo de moneda"
                                v-model="newOrder.SosCurrency"
                                @getDataInitial="getDataInitialCurrency($event)"
                              ></s-select-definition>
                            </v-col>
                            <v-col cols="12" md="3">
                              <s-select-definition @getDataInitial="getDataInitial" id="DedAbbreviation" return-object :def="1143" v-model="newOrder.ObjTax" @change="changeTax"/>
                            </v-col>
                            <v-col cols="12">
                              <s-textarea
                                v-model="newOrder.SosAdditionalDescription"
                                label="Descripción adicional"
                                :rows="2"
                              ></s-textarea>
                            </v-col>
                            <v-col cols="12" md="4">
                              <s-date
                                v-model="newOrder.SosRegistrationDate"
                                label="Fecha de registro"
                                type="date"
                                disabled
                              ></s-date>
                            </v-col>
                            <v-col cols="12" md="4">
                              <s-date
                                v-model="newOrder.SosEstimatedDeliveryDay"
                                label="Fecha de entrega estimada"
                                type="date"
                              ></s-date>
                            </v-col>
                            <v-col cols="12" md="4">
                              <s-date
                                v-model="newOrder.SosDeliveryDate"
                                label="Fecha de entrega real"
                                type="date"
                                disabled
                                clearable
                              ></s-date>
                            </v-col>
                            <v-col cols="12" class="d-flex flex-column align-end gap-2">
                              <h4 class="mr-2">Subtotal: {{ newOrder.SosSubtotal | numberDefault }}</h4>
                              <h4 class="mr-2">Impuesto: {{ newOrder.SosTaxAmount | numberDefault }}</h4>
                              <h4 class="mr-2">Total: {{ newOrder.SosTotal | numberDefault }}</h4>
                            </v-col>
                          </v-row>
                          <v-row>
                            <v-col cols="12" class="d-flex gap-2 justify-end">
                              <v-btn
                                @click="cancelAddOrder"
                                text
                                color="grey"
                                class="text-none"
                                small
                              >
                                Cancelar
                              </v-btn>
                              <v-btn
                                @click="addOrder"
                                color="primary"
                                class="text-none"
                                small
                              >
                                Guardar
                              </v-btn>
                            </v-col>
                          </v-row>
                        </v-card-text>
                      </v-card>

                      <v-list v-if="modalPendingOrders.length > 0" class="transparent">
                        <v-list-item
                          v-for="(pedido, index) in modalPendingOrders"
                          :key="`pendiente-${index}`"
                          class="pedido-item mb-3"
                        >
                          <v-card class="w-100" outlined style="width: 100%;">
                            <v-card-title class="pb-2">
                              <div class="d-flex justify-space-between align-center w-100" style="width: 100%;">
                                <div class="flex-grow-1">
                                  <h3 class="text-h6 font-weight-medium">{{ pedido.SosArtName }}</h3>
                                  <v-chip small color="primary" outlined class="mt-1">
                                    Código: {{ pedido.SosArtCode }}
                                  </v-chip>
                                </div>
                                <div class="d-flex gap-1">
                                  <v-btn
                                    @click="deleteOrder(pedido, index)"
                                    icon
                                    small
                                    color="red"
                                  >
                                    <v-icon small>mdi-delete</v-icon>
                                  </v-btn>
                                  <v-btn
                                    @click="editOrder(pedido, index)"
                                    icon
                                    small
                                    color="warning"
                                  >
                                    <v-icon small>mdi-pencil</v-icon>
                                  </v-btn>
                                  <v-btn
                                    @click="markAsCompleted(pedido, index)"
                                    icon
                                    small
                                    color="green"
                                  >
                                    <v-icon small>mdi-check</v-icon>
                                  </v-btn>
                                </div>
                              </div>
                            </v-card-title>

                            <v-card-text class="pt-0">
                              <!-- Descripción adicional si existe -->
                              <div v-if="pedido.SosAdditionalDescription" class="mb-3">
                                <v-icon small color="grey" class="mr-1">mdi-information</v-icon>
                                <span class="text-body-2">{{ pedido.SosAdditionalDescription }}</span>
                              </div>

                              <v-row no-gutters>
                                <!-- Cantidad y Unidad -->
                                <v-col cols="12" md="6" class="pr-md-2">
                                  <v-card flat color="blue-grey lighten-5" class="pa-3 mb-2">
                                    <div class="d-flex align-center">
                                      <v-icon color="blue" class="mr-2">mdi-package-variant</v-icon>
                                      <div>
                                        <div class="text-body-2 font-weight-medium">Cantidad</div>
                                        <div class="text-h6 primary--text">
                                          {{ pedido.SosAmount }} {{ pedido.SosUnitOfMeasuarement }}
                                        </div>
                                      </div>
                                    </div>
                                  </v-card>
                                </v-col>

                                <!-- Precios -->
                                <v-col cols="12" md="6" class="pl-md-2">
                                  <v-card flat color="green lighten-5" class="pa-3 mb-2">
                                    <div class="d-flex align-center">
                                      <v-icon color="green" class="mr-2">mdi-cash</v-icon>
                                      <div class="flex-grow-1">
                                        <div class="text-body-2 font-weight-medium">Total</div>
                                        <div class="text-h6 green--text text--darken-2">
                                          {{ pedido.SosCurrencyName }} {{ pedido.SosTotal | numberDefault }}
                                        </div>
                                      </div>
                                    </div>
                                  </v-card>
                                </v-col>
                              </v-row>

                              <!-- Detalles de precios -->
                              <v-expansion-panels flat class="mb-2">
                                <v-expansion-panel>
                                  <v-expansion-panel-header class="pa-2">
                                    <span class="text-body-2">
                                      <v-icon small class="mr-1">mdi-calculator</v-icon>
                                      Ver detalles de precios
                                    </span>
                                  </v-expansion-panel-header>
                                  <v-expansion-panel-content>
                                    <v-row dense>
                                      <v-col cols="6">
                                        <div class="text-caption grey--text">Precio unitario</div>
                                        <div class="text-body-2">{{ pedido.SosCurrencyName }} {{ pedido.SosUnitPrice | numberDefault }}</div>
                                      </v-col>
                                      <v-col cols="6">
                                        <div class="text-caption grey--text">Subtotal</div>
                                        <div class="text-body-2">{{ pedido.SosCurrencyName }} {{ pedido.SosSubtotal | numberDefault }}</div>
                                      </v-col>
                                      <v-col cols="6">
                                        <div class="text-caption grey--text">{{ pedido.SosTaxCode }} ({{ pedido.SosTax }}%)</div>
                                        <div class="text-body-2">{{ pedido.SosCurrencyName }} {{ pedido.SosTaxAmount | numberDefault }}</div>
                                      </v-col>
                                      <v-col cols="6">
                                        <div class="text-caption grey--text font-weight-bold">Total</div>
                                        <div class="text-body-1 font-weight-bold">{{ pedido.SosCurrencyName }} {{ pedido.SosTotal | numberDefault }}</div>
                                      </v-col>
                                    </v-row>
                                  </v-expansion-panel-content>
                                </v-expansion-panel>
                              </v-expansion-panels>

                              <!-- Fechas -->
                              <div class="d-flex flex-wrap justify-space-between gap-3 mt-2" style="width: 100%;">
                                <div class="d-flex align-center">
                                  <v-icon small color="blue" class="mr-1">mdi-calendar-plus</v-icon>
                                  <span class="text-caption">
                                    Registrado: {{ $fun.formatDateTimeView(pedido.SosRegistrationDate) }}
                                  </span>
                                </div>
                                
                                <div v-if="pedido.SosEstimatedDeliveryDay" class="d-flex align-center">
                                  <v-icon small color="purple" class="mr-1">mdi-calendar-end</v-icon>
                                  <span class="text-caption">
                                    Entrega estimada: {{ $fun.formatDateView(pedido.SosEstimatedDeliveryDay) }}
                                  </span>
                                </div>

                                <div v-if="pedido.SosDeliveryDate" class="d-flex align-center">
                                  <v-icon small color="green" class="mr-1">mdi-calendar-check</v-icon>
                                  <span class="text-caption">
                                    Entregado: {{ $fun.formatDateTimeView(pedido.SosDeliveryDate) }}
                                  </span>
                                </div>
                              </div>

                              <!-- IDs adicionales (si necesitas mostrarlos) -->
                              <div class="mt-2 d-flex flex-wrap gap-2">
                                <v-chip x-small outlined>Cultivo: {{ pedido.TypeCultiveName }}</v-chip>
                                <v-chip x-small outlined>Variedad: {{ pedido.VrtIDName }}</v-chip>
                                <v-chip x-small outlined>Calibre: {{ pedido.TypeCaliberName }}</v-chip>
                              </div>
                            </v-card-text>
                          </v-card>
                        </v-list-item>
                      </v-list>
                      <div v-else class="text-center pa-8">
                        <v-icon large color="grey lighten-2" class="mb-2">mdi-clipboard-list-outline</v-icon>
                        <p class="text-body-2 grey--text">No hay pedidos pendientes</p>
                      </div>
                    </div>
                  </v-tab-item>
                  <!-- Pedidos Atendidos -->
                  <v-tab-item>
                    <div class="pa-4">
                      <v-list v-if="modalOrdersServed.length > 0" class="transparent">
                        <v-list-item
                          v-for="(pedido, index) in modalOrdersServed"
                          :key="`atendido-${index}`"
                          class="pedido-item mb-3"
                        >
                          <v-card class="w-100" outlined style="width: 100%;">
                            <v-card-title class="pb-2">
                              <div class="d-flex justify-space-between align-center w-100" style="width: 100%;">
                                <div class="flex-grow-1">
                                  <h3 class="text-h6 font-weight-medium">{{ pedido.SosArtName }}</h3>
                                  <div class="d-flex align-center gap-2 mt-1">
                                    <v-chip small color="primary" outlined>
                                      Código: {{ pedido.SosArtCode }}
                                    </v-chip>
                                    <v-chip small color="green" class="white--text">
                                      <v-icon small left>mdi-check-circle</v-icon>
                                      Completado
                                    </v-chip>
                                  </div>
                                </div>
                              </div>
                            </v-card-title>

                            <v-card-text class="pt-0">
                              <!-- Descripción adicional si existe -->
                              <div v-if="pedido.SosAdditionalDescription" class="mb-3">
                                <v-icon small color="grey" class="mr-1">mdi-information</v-icon>
                                <span class="text-body-2">{{ pedido.SosAdditionalDescription }}</span>
                              </div>

                              <v-row no-gutters>
                                <!-- Cantidad y Unidad -->
                                <v-col cols="12" md="6" class="pr-md-2">
                                  <v-card flat color="blue-grey lighten-5" class="pa-3 mb-2">
                                    <div class="d-flex align-center">
                                      <v-icon color="blue" class="mr-2">mdi-package-variant</v-icon>
                                      <div>
                                        <div class="text-body-2 font-weight-medium">Cantidad</div>
                                        <div class="text-h6 primary--text">
                                          {{ pedido.SosAmount }} {{ pedido.SosUnitOfMeasuarement }}
                                        </div>
                                      </div>
                                    </div>
                                  </v-card>
                                </v-col>

                                <!-- Precios -->
                                <v-col cols="12" md="6" class="pl-md-2">
                                  <v-card flat color="green lighten-5" class="pa-3 mb-2">
                                    <div class="d-flex align-center">
                                      <v-icon color="green" class="mr-2">mdi-cash</v-icon>
                                      <div class="flex-grow-1">
                                        <div class="text-body-2 font-weight-medium">Total</div>
                                        <div class="text-h6 green--text text--darken-2">
                                          {{ pedido.SosCurrencyName }} {{ pedido.SosTotal | numberDefault }}
                                        </div>
                                      </div>
                                    </div>
                                  </v-card>
                                </v-col>
                              </v-row>

                              <!-- Detalles de precios -->
                              <v-expansion-panels flat class="mb-2">
                                <v-expansion-panel>
                                  <v-expansion-panel-header class="pa-2">
                                    <span class="text-body-2">
                                      <v-icon small class="mr-1">mdi-calculator</v-icon>
                                      Ver detalles de precios
                                    </span>
                                  </v-expansion-panel-header>
                                  <v-expansion-panel-content>
                                    <v-row dense>
                                      <v-col cols="6">
                                        <div class="text-caption grey--text">Precio unitario</div>
                                        <div class="text-body-2">{{ pedido.SosCurrencyName }} {{ pedido.SosUnitPrice | numberDefault }}</div>
                                      </v-col>
                                      <v-col cols="6">
                                        <div class="text-caption grey--text">Subtotal</div>
                                        <div class="text-body-2">{{ pedido.SosCurrencyName }} {{ pedido.SosSubtotal | numberDefault }}</div>
                                      </v-col>
                                      <v-col cols="6">
                                        <div class="text-caption grey--text">{{ pedido.SosTaxCode }} ({{ pedido.SosTax }}%)</div>
                                        <div class="text-body-2">{{ pedido.SosCurrencyName }} {{ pedido.SosTaxAmount | numberDefault }}</div>
                                      </v-col>
                                      <v-col cols="6">
                                        <div class="text-caption grey--text font-weight-bold">Total</div>
                                        <div class="text-body-1 font-weight-bold">{{ pedido.SosCurrencyName }} {{ pedido.SosTotal | numberDefault }}</div>
                                      </v-col>
                                    </v-row>
                                  </v-expansion-panel-content>
                                </v-expansion-panel>
                              </v-expansion-panels>

                              <!-- Fechas -->
                              <div class="d-flex flex-wrap justify-space-between gap-3 mt-2" style="width: 100%;">
                                <div class="d-flex align-center">
                                  <v-icon small color="blue" class="mr-1">mdi-calendar-plus</v-icon>
                                  <span class="text-caption">
                                    Registrado: {{ $fun.formatDateTimeView(pedido.SosRegistrationDate) }}
                                  </span>
                                </div>
                                
                                <div v-if="pedido.SosEstimatedDeliveryDay" class="d-flex align-center">
                                  <v-icon small color="purple" class="mr-1">mdi-calendar-end</v-icon>
                                  <span class="text-caption">
                                    Entrega estimada: {{ $fun.formatDateView(pedido.SosEstimatedDeliveryDay) }}
                                  </span>
                                </div>

                                <div v-if="pedido.SosDeliveryDate" class="d-flex align-center">
                                  <v-icon small color="green" class="mr-1">mdi-calendar-check</v-icon>
                                  <span class="text-caption">
                                    Entregado: {{ $fun.formatDateTimeView(pedido.SosDeliveryDate) }}
                                  </span>
                                </div>
                              </div>

                              <!-- IDs adicionales -->
                              <div class="mt-2 d-flex flex-wrap gap-2">
                                <v-chip x-small outlined>Cultivo: {{ pedido.TypeCultiveName }}</v-chip>
                                <v-chip x-small outlined>Variedad: {{ pedido.VrtIDName }}</v-chip>
                                <v-chip x-small outlined>Calibre: {{ pedido.TypeCaliberName }}</v-chip>
                              </div>
                            </v-card-text>
                          </v-card>
                        </v-list-item>
                      </v-list>
                      <div v-else class="text-center pa-8">
                        <v-icon large color="grey lighten-2" class="mb-2">mdi-clipboard-check-outline</v-icon>
                        <p class="text-body-2 grey--text">No hay pedidos atendidos</p>
                      </div>
                    </div>
                  </v-tab-item>
                </v-tabs-items>
              </v-card-text>
      
              <v-divider></v-divider>
      
              <v-card-actions class="pa-4">
                <v-spacer></v-spacer>
                <v-btn
                  @click="closeModal"
                  text
                  color="grey"
                  class="text-none"
                >
                  Cerrar
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
      

          <!-- Modal para registrar nuevo cliente -->
          <v-dialog
            v-model="showCampaignModal"
            max-width="600px"
            persistent
            scrollable
          >
            <v-card class="modal-card">
              <v-card-title class="pa-6 pb-4">
                <div class="d-flex align-center justify-space-between w-100" style="width: 100%;">
                  <div>
                    <h2 class="text-h5 font-weight-regular grey--text text--darken-3">
                      <v-icon left color="primary">mdi-bullhorn</v-icon>
                    {{ newCustomer.CsoID ? 'Editar Cliente' : 'Registrar Cliente' }}
                    </h2>
                    <p class="text-body-2 grey--text text--darken-1 mb-0 mt-1">
                      Complete todos los campos requeridos
                    </p>
                  </div>
                  <v-btn
                    @click="closeCampaignModal"
                    icon
                    large
                    color="grey"
                  >
                    <v-icon>mdi-close</v-icon>
                  </v-btn>
                </div>
              </v-card-title>
      
              <v-divider></v-divider>
      
              <v-card-text class="pa-6">
                <v-form ref="campaignForm" v-model="validCampaignForm" class="customer-form">
                  <v-row>
                    <!-- Campaña -->
                    <v-col cols="12">
                      <s-select-campaign-area
                        v-if="showCampaignModal"
                        v-model="newCustomer.CmaID"
                        label="Campaña"
                        typeArea="1"
                        full
                      />
                    </v-col>

                    <!-- Cliente -->
                    <v-col cols="12" style="display: flex; align-items: end; gap: 10px">
                      <s-select-customer
                        v-if="(!newCustomer.CsoID && !noEdit) || (newCustomer.CsoID && noEdit)"
                        v-model="newCustomer.CardCode"
                        label="Cliente"
                        clearable
                        style="flex: 1;"
                      />
                      <s-text style="flex: 1;" v-if="newCustomer.CsoID && !noEdit" v-model="newCustomer.CardName" label="Cliente" readonly></s-text>
                      <v-btn color="primary" class="text-none" small v-if="newCustomer.CsoID && !noEdit" @click="noEdit = true"><v-icon>mdi-account-edit</v-icon></v-btn>
                      <v-btn color="red" class="text-none" small v-if="newCustomer.CsoID && noEdit" @click="noEdit = false"><v-icon>mdi-account-cancel</v-icon></v-btn>
                    </v-col>

                    <!-- Prioridad -->
                    <v-col cols="12" md="6">
                      <s-select-definition
                        :def="1588"
                        label="Prioridad"
                        v-model="newCustomer.CsoPriority"
                        />

                    </v-col>

                    <!-- Continente -->
                    <v-col cols="12" md="6">
                      <s-select-definition
                        :def="1589"
                        label="Continente"
                        v-model="newCustomer.CsoContinent"
                        />
                    </v-col>

                    <!-- Orden -->
                    <!-- <v-col cols="12" md="6">
                      <s-text
                        v-model="newCustomer.CsoOrder"
                        label="Número de Orden"
                        type="number"
                        :rules="[
                          v => !!v || 'El número de CsoOrder es requerido',
                          v => v > 0 || 'El número debe ser mayor a 0'
                        ]"
                      />
                    </v-col> -->

                    <!-- Color (Color Picker) -->
                    <!-- <v-col cols="12" md="6">
                      <label class="text-subtitle-2 grey--text text--darken-2 mb-2 d-block">
                        <v-icon small color="palette" class="mr-1">mdi-palette</v-icon>
                        Color de Cliente
                      </label>
                      <div class="color-picker-container">
                        <v-color-picker
                          v-model="newCustomer.CsoColor"
                          class="mb-2"
                          hide-inputs
                          hide-mode-switch
                          elevation="2"
                        ></v-color-picker>
                        <div class="color-info d-flex align-center mt-2">
                          <div 
                            class="color-preview-large mr-3"
                            :style="{ backgroundColor: newCustomer.CsoColor }"
                          ></div>
                          <v-text-field
                            v-model="newCustomer.CsoColor"
                            label="Código Hex"
                            dense
                            outlined
                            class="hex-input"
                            :rules="[
                              v => !!v || 'El color es requerido',
                              v => /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(v) || 'Formato de color inválido'
                            ]"
                          ></v-text-field>
                        </div>
                      </div>
                    </v-col> -->
                  </v-row>
                </v-form>
              </v-card-text>
      
              <v-divider></v-divider>
      
              <v-card-actions class="pa-4">
                <v-spacer></v-spacer>
                <v-btn
                  @click="closeCampaignModal"
                  text
                  color="grey"
                  class="text-none"
                >
                  Cancelar
                </v-btn>
                <v-btn
                  @click="saveCampaign"
                  color="primary"
                  class="text-none"
                  :disabled="!validCampaignForm"
                >
                  <v-icon left small>mdi-content-save</v-icon>
                  {{ newCustomer.CsoID ? 'Actualizar' : 'Registrar' }}
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </v-container>
    </v-card>
</template>

<script>
import draggable from 'vuedraggable'
import SSelectCampaignArea from "../../../components/Utils/SSelectCampaignArea.vue";
import SSelectCustomer from "../../../components/Sale/SselectClientSap.vue";
import SText from '@/components/Utils/SText.vue'
import SleCampaignSalesOrderService from '../../../services/Sale/SleCampaignSalesOrderService';
import sArticleSearch from "@/components/Utils/Logistics/SArticleSearch";
import SSelectVariety from "@/components/HarvestSchedule/SSelectVariety";
import SleSalesOrdersService from '../../../services/Sale/SleSalesOrdersService';

export default {
  name: 'CampaignManagement',
  components: {
    draggable,
    SSelectCampaignArea,
    SSelectCustomer,
    sArticleSearch,
    SSelectVariety
  },
  data() {
    return {
      isDragMode: false,
      showModal: false,
      showCampaignModal: false,
      showAddOrderForm: false,
      selectedCampaign: null,
      editingCampaign: null,
      activeTab: 0,
      validCampaignForm: false,
      noEdit: false,
      hasInitialDataLoaded: false, // Flag para controlar si ya se cargó la data inicial
      
      // Variables reactivas separadas para las órdenes
      modalPendingOrders: [],
      modalOrdersServed: [],
      
      // Sistema de rastreo de cambios
      modifiedCustomers: new Set(), // Set para almacenar IDs de clientes modificados
      hasChanges: false, // Flag para indicar si hay cambios pendientes
      
      priorities: [
        { key: 1, label: 'Prioridad Alta', color: 'red' },
        { key: 2, label: 'Prioridad Media', color: 'orange' },
        { key: 3, label: 'Prioridad Baja', color: 'green' }
      ],

      newOrder: {
        OrderArticle: null,
        SosUnitOfMeasuarement: '',
        SosUserUnitOfMeasuarement: '',
        SosArtCode: '',
        SosArtName: '',
        SosCurrency: 0,
        SosUnitPrice: 0,
        ObjTax: null,
        SosTaxCode: '',
        SosTax: 0,
        SosTaxAmount: 0,
        SosSubtotal: 0,
        SosTotal: 0,
        SosAdditionalDescription: '',
        SosAmount: 0,
        SosEstimatedDeliveryDay: this.$fun.getDate(),
        SosDeliveryDate: null,
        SosRegistrationDate: this.$fun.getDateTimeShortDB(),
        TypeCultive : null,
        VrtID : null,
        TypeCaliber : null,
        SosStatus : 1
      },

      newCustomer: {
        CsoID: 0,
        CmaID: null,
        CmaName: null,
        CardCode: '',
        CardName: '',
        CsoContinent: 1,
        CsoPriority: 2,
        CsoOrder: 1,
        CsoColor: '#FF8C00'
      },
      filter: {
        CmaID: null,
        CsoContinent: null
      },
      Customers: [],
      TaxItems: [],
      CultivoItems: [],
      VarietyItems: [],
      CurrencyItems: [],
      cultivoVariants: {
        PALTO: ["palta", "paltas", "hass", "avocado"], 
        MANGO: ["mango", "mangos"],
        UVA: ["uva", "uvas", "grape", "grapes"],
        ARANDANO: ["arandano", "arándano", "arandanos", "arándanos", "blueberry", "blueberries"],
        MANDARINA: ["mandarina", "mandarinas", "tangerine", "tangerines"],
        AGUAYMANTO: ["aguaymanto", "golden berry", "goldenberries"],
      },
      editDataOrder: false
    }
  },
  
  computed: {
    campaignsByPriority() {
      const grouped = {
        1: [], // Prioridad Alta
        2: [], // Prioridad Media
        3: []  // Prioridad Baja
      }
      
      this.Customers.forEach(customer => {
        if (grouped[customer.CsoPriority]) {
          grouped[customer.CsoPriority].push(customer)
        }
      })
      
      // Ordenar por campo 'CsoOrder' dentro de cada CsoPriority
      Object.keys(grouped).forEach(priority => {
        grouped[priority].sort((a, b) => a.CsoOrder - b.CsoOrder)
      })
      
      return grouped
    },

  },
  
  methods: {
    refreshData(){        
        SleCampaignSalesOrderService.list(this.filter, this.$fun.getUserID()).then(response => {
            if(response.status == 200){
              console.log('response:', response)
              this.Customers = response.data;
            }
        })
    },
    refreshDataOrder(){
      if(!this.selectedCampaign.CsoID) return;
      const filterOrder = {CsoID : this.selectedCampaign.CsoID};
      SleSalesOrdersService.list(filterOrder, this.$fun.getUserID()).then(response => {
          if(response.status == 200){
            console.log('response:', response.data)
            // Asignar a las variables reactivas separadas
            if(response.data.length > 0) {
              this.modalPendingOrders = response.data.filter(order => order.SosStatus == 1);
              this.modalOrdersServed = response.data.filter(order => order.SosStatus == 2);
            } else {
              this.modalPendingOrders = [];
              this.modalOrdersServed = [];
            }
            
            console.log('this.modalPendingOrders', this.modalPendingOrders)
            console.log('this.modalOrdersServed', this.modalOrdersServed)
          }
      })
    },
    async toggleDragMode() {
      if (this.isDragMode && this.hasChanges) {
        // Si está saliendo del modo drag y hay cambios, preguntar si quiere guardar
        const result = await this.$fun.alert("Se detectaron cambios. ¿Desea guardar los cambios realizados?", "question", {
          showCancelButton: true,
          confirmButtonText: 'Guardar',
          cancelButtonText: 'Descartar'
        });
        
        if (result.value) {
          // Guardar cambios
          await this.saveModifiedCustomers();
        } else if (result.dismiss === 'cancel') {
          // Descartar cambios - recargar datos
          this.refreshData();
          this.resetChangeTracking();
        }
      }
      
      // Cambiar el modo
      this.isDragMode = !this.isDragMode;
      
      // Si está entrando al modo drag, resetear el rastreo de cambios
      if (this.isDragMode) {
        this.resetChangeTracking();
      }
    },
    
    getCampaignsByPriority(priority) {
      return this.campaignsByPriority[priority] || []
    },

    updateCampaignsByPriority(priority, newList) {
      // Solo procesar si estamos en modo drag
      if (!this.isDragMode) return;
      
      // Actualizar inmediatamente la lista
      newList.forEach((customer, index) => {
        const oldPriority = customer.CsoPriority;
        const oldOrder = customer.CsoOrder;
        
        customer.CsoPriority = priority;
        customer.CsoOrder = index + 1;
        
        // Marcar el cliente como modificado si hubo cambio
        if (oldPriority !== priority || oldOrder !== (index + 1)) {
          this.modifiedCustomers.add(customer.CsoID);
          this.hasChanges = true;
        }
      })

      // Encontrar y actualizar las campañas en el array principal
      this.Customers = this.Customers.map(customer => {
        const updatedCampaign = newList.find(c => c.CsoID === customer.CsoID);
        return updatedCampaign || customer;
      });
      
      // No guardamos inmediatamente, solo acumulamos cambios
      console.log('Cambios acumulados:', Array.from(this.modifiedCustomers));
    },

    saveCampaignOrder() {
      // Aquí haces la llamada a tu API para guardar en la base de datos
      console.log('Guardando CsoOrder en BD:', this.Customers.map(c => ({
        CsoID: c.CsoID,
        CsoPriority: c.CsoPriority,
        CsoOrder: c.CsoOrder
      })))
      
      // Ejemplo de llamada API:
      // this.$http.post('/api/Customers/update-order', { Customers: this.Customers })
    },
    
    getPriorityColor(priority) {
      const colors = {
        1: 'red', // Prioridad Alta
        2: 'orange', // Prioridad Media
        3: 'green'  // Prioridad Baja
      }
      return colors[priority] || 'grey'
    },
    
    onDragChange(event, priority) {
      console.log('Cambio en drag:', event, priority)
      // La actualización se maneja en updateCampaignsByPriority
    },
    
    openModal(customer) {
      if (!this.isDragMode) {
        this.selectedCampaign = customer;
        this.refreshDataOrder();
        this.showModal = true
        this.activeTab = 0
        this.showAddOrderForm = false
      }
    },
    
    closeModal() {
      this.showModal = false
      this.selectedCampaign = null
      this.activeTab = 0
      this.showAddOrderForm = false
      this.resetNewOrder();
    },


    resetNewOrder() {
      this.newOrder = {
        OrderArticle: null,
        SosUnitOfMeasuarement: '',
        SosUserUnitOfMeasuarement: '',
        SosArtCode: '',
        SosArtName: '',
        SosCurrency: 0,
        SosUnitPrice: 0,
        ObjTax: null,
        SosTaxCode: '',
        SosTax: 0,
        SosTaxAmount: 0,
        SosSubtotal: 0,
        SosTotal: 0,
        SosAdditionalDescription: '',
        SosAmount: 0,
        SosEstimatedDeliveryDay: this.$fun.getDate(),
        SosDeliveryDate: null,
        SosRegistrationDate: this.$fun.getDateTimeShortDB(),
        TypeCultive : null,
        VrtID : null,
        TypeCaliber : null,
        SosStatus: 1
      }
    },
    changeTax(items) {
      if(!this.newOrder.ObjTax) return;
      const itemTax = items.find(x => x.DedAbbreviation === this.newOrder.ObjTax.DedAbbreviation);
      console.log("Data del Impuesto:",itemTax)
      this.newOrder.SosTaxCode = itemTax.DedAbbreviation;
      this.newOrder.SosTax = itemTax.DedHelper ? parseFloat(itemTax.DedHelper) : 0;
      this.calculated();
    },
    getDataInitial(items) {
      console.log("Data de getDataInitial:",items)
      this.TaxItems = items;
    },
    getDataInitialCultivo(items) {
      console.log("Data de getDataInitialCultivo:",items)
      this.CultivoItems = items;
    },
    getDataInitialCurrency(items) {
      console.log("Data de getDataInitialCurrency:",items)
      this.CurrencyItems = items;
    },
    changeVariety(items) {
      if(!this.newOrder.TypeCultive) return;
      console.log("Data de changeVariety:",items)
      this.VarietyItems = items;
    },
    async seletedData(item){
      console.log("Data del Articulo:",item)
      if(!item) return;
      this.newOrder.SosArtCode = item.ArtCode;
      this.newOrder.SosUnitOfMeasuarement = item.UnitOfMeasuarement;
      this.newOrder.SosUserUnitOfMeasuarement = item.UserUnitOfMeasuarement;
      this.newOrder.SosArtName = item.ArtName;
      this.newOrder.SosUnitPrice = item.LastPurPrice;
      this.newOrder.SosTaxCode = item.TaxCodeAP;
      this.newOrder.SosAmount = 0;

      const itemCurrency = this.CurrencyItems.find(x => x.DedHelper.toLowerCase() === item.LastPurCurrency.toLowerCase());
      this.newOrder.SosCurrency = itemCurrency.DedValue ? parseFloat(itemCurrency.DedValue) : null;
      
      const itemTax = this.TaxItems.find(x => x.DedAbbreviation.toLowerCase() === this.newOrder.SosTaxCode.toLowerCase());
      console.log("Data del Impuesto  en seletedData:",itemTax)
      this.newOrder.SosTax = itemTax.DedHelper ? parseFloat(itemTax.DedHelper) : 0;
      this.calculated();

      console.log("Data del CultivoItems:",this.CultivoItems)
      this.newOrder.TypeCultive = this.changeCultivoSelected();

      // Esperar a que se carguen las variedades antes de seleccionar una
      if (this.newOrder.TypeCultive) {
        await this.waitForVarietyItems();
        console.log("Data del VarietyItems después de esperar:",this.VarietyItems)
        this.newOrder.VrtID = this.changeVarietySelected();
      } else {
        this.newOrder.VrtID = null;
      }
    },

    changeCultivoSelected() {
      const name = this.newOrder.SosArtName.toLowerCase();

      const cultivo = this.CultivoItems.find(c => {
        const variants = this.cultivoVariants[c.DedDescription.toUpperCase()] || [];
        return variants.some(v => name.includes(v));
      });

      if (cultivo) {
        console.log("✅ Cultivo detectado:", cultivo.DedDescription, "| DedValue:", cultivo.DedValue);
        return cultivo.DedValue;
      } else {
        console.warn("⚠️ No se encontró cultivo en el nombre:", this.newOrder.SosArtName);
        return null;
      }
    },

    changeVarietySelected() {
      const name = this.newOrder.SosArtName.toLowerCase();

      const variety = this.VarietyItems.find(v =>
        name.includes(v.VrtDescription.toLowerCase())
      );

      if (variety) {
        console.log("✅ Variedad detectada:", variety.VrtDescription, "| VrtID:", variety.VrtID);
        return variety.VrtID;
      } else {
        console.warn("⚠️ No se encontró variedad en el nombre:", this.newOrder.SosArtName);
        return null;
      }
    },

    // Método para esperar a que se carguen las variedades
    waitForVarietyItems() {
      return new Promise((resolve) => {
        // Si ya hay variedades cargadas, resolver inmediatamente
        if (this.VarietyItems && this.VarietyItems.length > 0) {
          console.log("✅ VarietyItems ya están cargados", this.VarietyItems.length, "items");
          resolve();
          return;
        }

        // Configurar un watcher temporal para detectar cuando se cargan las variedades
        const unwatch = this.$watch('VarietyItems', (newValue) => {
          if (newValue && newValue.length > 0) {
            console.log("✅ VarietyItems se han cargado", newValue.length, "items");
            unwatch(); // Eliminar el watcher
            resolve();
          }
        }, { immediate: true });

        // Timeout de seguridad para evitar esperar indefinidamente
        setTimeout(() => {
          console.warn("⚠️ Timeout esperando VarietyItems, resolviendo de todas formas");
          unwatch();
          resolve();
        }, 5000); // 5 segundos de timeout
      });
    },

    addOrder() {

      if(!this.selectedCampaign.CsoID) return;

      if(!this.newOrder.SosArtName || this.newOrder.SosArtName == ''){
        this.$fun.alert('No se ha seleccionado un articulo', 'warning');
        return;
      }

      if(!this.newOrder.SosAmount || this.newOrder.SosAmount == 0 || this.newOrder.SosAmount == ''){
        this.$fun.alert('La cantidad no puede estar vacia o ser 0', 'warning');
        return;
      }

      if(!this.newOrder.SosUnitPrice || this.newOrder.SosUnitPrice == 0 || this.newOrder.SosUnitPrice == ''){
        this.$fun.alert('El precio no puede estar vacio o ser 0', 'warning');
        return;
      }

      this.newOrder.CsoID = this.selectedCampaign.CsoID
      this.newOrder.UsrCreateID = this.$fun.getUserID();
      this.newOrder.UsrUpdateID = this.$fun.getUserID();
      console.log('Pedido agregado:', this.newOrder)
      
      // Aquí puedes hacer llamada API para guardar

      this.$fun.alert("¿Estas seguro de guardar?", "question").then(async (r) => {
        if(r.value){
          SleSalesOrdersService.save(this.newOrder, this.$fun.getUserID()).then(response => {
            if(response.status == 200){
              if(this.newOrder.SosID){
                this.$fun.alert('Pedido actualizado correctamente', 'success');
              }else{
                this.$fun.alert('Pedido agregado correctamente', 'success');
              }
              this.showAddOrderForm = false;
              this.resetNewOrder();
              this.refreshDataOrder();
              this.refreshData();
            }
          })
        }
      })
    },

    cancelAddOrder() {
      this.resetNewOrder()
      this.showAddOrderForm = false;
    },

    deleteOrder(pedido, index) {
      const OrderDelete = {
        ...pedido,
        UsrCreateID : this.$fun.getUserID(),
        UsrUpdateID : this.$fun.getUserID()
      }

      console.log('Pedido eliminado', OrderDelete)
      // Aquí puedes hacer llamada API para eliminar

      this.$fun.alert("¿Estas seguro de eliminar?", "question").then(async (r) => {
        if(r.value){
          SleSalesOrdersService.save(OrderUpdate, this.$fun.getUserID()).then(response => {
            if(response.status == 200){
              this.$fun.alert('Pedido agregado correctamente', 'success');
              this.showAddOrderForm = false;
              this.resetNewOrder();
              this.refreshDataOrder();
              this.refreshData();
            }
          })
        }
      })
    },

    editOrder(pedido, index) {
      this.showAddOrderForm = true;
      this.editDataOrder = true;
      this.newOrder = { ...pedido };
    },
    
    markAsCompleted(pedido, index) {
      // Mover pedido de pendientes a atendidos

      const OrderUpdate = {
        ...pedido,
        SosDeliveryDate: this.$fun.getDateTimeShortDB(),
        SosStatus: 2,
        UsrCreateID : this.$fun.getUserID(),
        UsrUpdateID : this.$fun.getUserID()
      }

      console.log('Pedido completado:', OrderUpdate)
      // Aquí puedes hacer llamada API para actualizar

      this.$fun.alert("¿Estas seguro de marcar como completado?", "question").then(async (r) => {
        if(r.value){
          SleSalesOrdersService.save(OrderUpdate, this.$fun.getUserID()).then(response => {
            if(response.status == 200){
              this.$fun.alert('Pedido agregado correctamente', 'success');
              this.showAddOrderForm = false;
              this.resetNewOrder();
              this.refreshDataOrder();
              this.refreshData();
            }
          })
        }
      })
    },


    closeCampaignModal() {
      this.showCampaignModal = false
      this.resetNewCampaign()
    },

    resetNewCampaign() {
      // Calcular el próximo número de CsoOrder disponible
      const maxOrder = Math.max(
        ...this.Customers.map(c => c.CsoOrder || 0),
        0
      )
      
      this.newCustomer = {
        CsoID: 0,
        CmaID: null,
        CmaName: null,
        CardCode: '',
        CardName: '',
        CsoPriority: 2,
        CsoContinent: 1,
        CsoOrder: maxOrder + 1,
        CsoColor: '#FF8C00'
      }
    },

    saveCampaign() {
        console.log('newCustomer:', this.newCustomer)
        // Verificar que todos los campos requeridos estén completos
        if (!this.newCustomer.CmaID || !this.newCustomer.CardCode) {
          this.$fun.alert('Complete todos los campos requeridos', 'error');
          return
        }

        const newCustomerObj = {...this.newCustomer}

        newCustomerObj.UsrCreateID = this.$fun.getUserID();
        newCustomerObj.UsrUpdateID = this.$fun.getUserID();
        
        console.log('Cliente:', newCustomerObj)
        // Aquí puedes hacer llamada API para guardar
        this.$fun.alert("¿Estas seguro de guardar?", "question").then(async (r) => {
            if(r.value){
                const response = await SleCampaignSalesOrderService.save(newCustomerObj, this.$fun.getUserID());

                if(response.status == 200){
                    this.closeCampaignModal();
                    this.refreshData();
            
                    // Mostrar mensaje de éxito
                    if(newCustomerObj.CsoID){
                    this.$fun.alert('Cliente Actualizado', 'success');
                    }else{
                        this.$fun.alert('Cliente Registrado', 'success');
                    }
                }
            }
        })
    },

    // Métodos para manejo de cambios
    resetChangeTracking() {
      this.modifiedCustomers.clear();
      this.hasChanges = false;
    },
    
    async saveModifiedCustomers() {
      if (this.modifiedCustomers.size === 0) {
        console.log('No hay cambios para guardar');
        return;
      }
      
      try {
        // Obtener solo los clientes modificados
        const modifiedCustomersData = this.Customers
          .filter(customer => this.modifiedCustomers.has(customer.CsoID));
        
        console.log('Guardando clientes modificados:', modifiedCustomersData.length);
        
        let savedCount = 0;
        let errorCount = 0;
        
        // Recorrer elemento por elemento para guardar
        for (const customer of modifiedCustomersData) {
          try {
            console.log(`Guardando cliente ID: ${customer.CsoID}`);
            
            // Crear objeto con los datos del cliente para guardar
            const newCustomerObj = {...customer};

            newCustomerObj.UsrCreateID = this.$fun.getUserID();
            newCustomerObj.UsrUpdateID = this.$fun.getUserID();
            
            const response = await SleCampaignSalesOrderService.save(newCustomerObj, this.$fun.getUserID());
            
            if (response.status === 200) {
              savedCount++;
              console.log(`Cliente ${customer.CsoID} guardado exitosamente`);
            } else {
              errorCount++;
              console.error(`Error al guardar cliente ${customer.CsoID}:`, response);
            }
            
          } catch (customerError) {
            errorCount++;
            console.error(`Error al guardar cliente ${customer.CsoID}:`, customerError);
          }
        }
        
        // Mostrar resultado final
        if (savedCount > 0 && errorCount === 0) {
          this.$fun.alert(`Se guardaron exitosamente ${savedCount} cliente(s)`, 'success');
          this.refreshData();
          this.resetChangeTracking();
        } else if (savedCount > 0 && errorCount > 0) {
          this.$fun.alert(`Se guardaron ${savedCount} cliente(s). ${errorCount} cliente(s) tuvieron errores.`, 'warning');
          // Solo resetear los que se guardaron exitosamente sería ideal, pero por simplicidad reseteamos todo
          this.refreshData();
          this.resetChangeTracking();
        } else {
          throw new Error(`No se pudo guardar ningún cliente. ${errorCount} errores.`);
        }
        
      } catch (error) {
        console.error('Error general al guardar cambios:', error);
        this.$fun.alert('Error al guardar los cambios. Inténtelo de nuevo.', 'error');
      }
    },

    // Métodos para edición de clientes
    editCampaign(customer) {
        this.newCustomer = { ...customer }
        console.log('newCustomer:', this.newCustomer)

        this.showCampaignModal = true
    },

    // Método para eliminar cliente
    async deleteCustomer(customer) {
      try {
        // Mostrar diálogo de confirmación
        const result = await this.$fun.alert(
          `¿Está seguro de eliminar el cliente "${customer.CardName}"?\nEsta acción no se podrá deshacer.`,
          "question"
        );

        // Si el usuario confirma la eliminación
        if (result.value) {
          console.log('Eliminando cliente:', customer.CsoID);
          
          // Crear una copia del cliente y cambiar SecStatus a 0
          const customerToDelete = {
            ...customer,
            SecStatus: 0
          };
          
          console.log('Cliente a eliminar:', customerToDelete);

          customerToDelete.UsrCreateID = this.$fun.getUserID();
          customerToDelete.UsrUpdateID = this.$fun.getUserID();
          
          // Llamar al servicio para guardar el cambio
          const response = await SleCampaignSalesOrderService.save(customerToDelete, this.$fun.getUserID());
          
          if (response.status === 200) {
            // Mostrar mensaje de éxito
            this.$fun.alert('Cliente eliminado exitosamente', 'success');
            
            // Refrescar los datos para reflejar el cambio
            this.refreshData();
            
            console.log(`Cliente ${customer.CsoID} eliminado exitosamente`);
          } else {
            throw new Error(`Error en la respuesta del servidor: ${response.status}`);
          }
        }
      } catch (error) {
        console.error('Error al eliminar cliente:', error);
        this.$fun.alert('Error al eliminar el cliente. Inténtelo de nuevo.', 'error');
      }
    },

    // Métodos para manejo de filtros
    applyFilters() {
      console.log('Aplicando filtros:', this.filter);
      this.refreshData();
    },

    // Método llamado cuando s-select-campaign-area se monta
    onCampaignAreaMounted() {
      console.log('s-select-campaign-area se ha montado completamente');
      
      // Ejecutar refreshData inicial si aún no se ha ejecutado
      if (!this.hasInitialDataLoaded) {
        console.log('Ejecutando refreshData inicial después de montar campaign area');
        // Usar un pequeño delay para asegurar que el componente esté completamente inicializado
        this.$nextTick(() => {
          setTimeout(() => {
            this.refreshData();
            this.hasInitialDataLoaded = true;
          }, 1000);
        });
      }
    },
    calculated(){
      this.newOrder.SosSubtotal = this.newOrder.SosUnitPrice * this.newOrder.SosAmount;
      this.newOrder.SosTaxAmount = (this.newOrder.SosSubtotal * this.newOrder.SosTax)/100;
      this.newOrder.SosTotal = this.newOrder.SosSubtotal + this.newOrder.SosTaxAmount;
    }
  },

  watch: {
    'newOrder.SosAmount'(newValue) {
      console.log('SosAmount cambiado a:', newValue);
      this.calculated();
    },
    'newOrder.SosUnitPrice'(newValue) {
      console.log('SosUnitPrice cambiado a:', newValue);
      this.calculated();
    }
  },
  
  mounted() {
    console.log('Componente montado, esperando a que s-select-campaign-area se monte...');
    
    // Timeout de respaldo por si el hook:mounted del componente no se ejecuta
    setTimeout(() => {
      if (!this.hasInitialDataLoaded) {
        console.log('Timeout alcanzado - ejecutando refreshData de respaldo');
        this.refreshData();
        this.hasInitialDataLoaded = true;
      }
    }, 100); // 1 segundo de timeout de respaldo
  }
}
</script>

<style scoped>
.priority-sections {
  max-width: 100%;
}

.section-card {
  border-radius: 16px !important;
}

.section-header {
  position: relative;
}

.priority-indicator {
  width: 4px;
  height: 24px;
  border-radius: 2px;
}

.priority-indicator.priority-1 {
  background-color: #f44336;
}

.priority-indicator.priority-2 {
  background-color: #ff9800;
}

.priority-indicator.priority-3 {
  background-color: #4caf50;
}

.cards-wrapper {
  border-radius: 16px !important;
  transition: all 0.3s ease;
}

.cards-container {
  /* min-height: 100px; */
  border-radius: 8px;
  transition: all 0.3s ease;
}

.cards-container.drag-enabled {
  background-color: rgba(33, 150, 243, 0.05);
  border: 2px dashed #2196f3;
  border-radius: 12px;
  padding: 8px;
}

.customer-card.draggable-card {
  cursor: grab;
  border-left-color: #2196f3;
  box-shadow: 0 4px 12px rgba(33, 150, 243, 0.2) !important;
}

.customer-card.draggable-card:active {
  cursor: grabbing;
  transform: rotate(2deg) scale(1.02);
}

.client-color-indicator,
.customer-color-indicator {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  border: 2px solid white;
}

.customer-info h3 {
  line-height: 1.3;
}

.customer-details {
  border-top: 1px solid #f5f5f5;
  padding-top: 12px;
}

.empty-section {
  border-radius: 16px;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border: 1px solid #dee2e6;
}

.modal-card {
  border-radius: 20px !important;
  overflow: hidden;
}

.tabs-container {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
}

.color-option {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  cursor: pointer;
  border: 3px solid transparent;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.color-option:hover {
  transform: scale(1.1);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
}

.color-option.selected {
  border-color: #2196f3;
  transform: scale(1.15);
  box-shadow: 0 0 0 3px rgba(33, 150, 243, 0.3);
}

.gap-2 {
  gap: 8px;
}

/* Animaciones para el drag & drop */
.flip-list-move {
  transition: transform 0.5s ease;
}

.flip-list-enter-active,
.flip-list-leave-active {
  transition: all 0.5s ease;
}

.flip-list-enter {
  opacity: 0;
  transform: translateY(20px) scale(0.9);
}

.flip-list-leave-to {
  opacity: 0;
  transform: translateY(-20px) scale(0.9);
}

/* Efectos de hover mejorados */
.v-btn:hover {
  transform: translateY(-1px);
}

.v-card:hover {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Responsive mejorado */
@media (max-width: 960px) {
  .customer-card {
    width: 100%;
    max-width: 400px;
  }
  
  .cards-container .d-flex {
    flex-direction: column;
    align-items: center;
  }

  .d-flex.justify-space-between.flex-wrap {
    flex-direction: column;
    gap: 12px;
  }

  .d-flex.gap-2.flex-wrap {
    justify-content: center;
  }
}

@media (max-width: 600px) {
  .pa-6 {
    padding: 16px !important;
  }
  
  .customer-card {
    margin: 4px !important;
  }
}

/* Mejoras visuales adicionales */
.v-chip {
  font-weight: 500;
}

.text-h4 {
  background: linear-gradient(135deg, #2196f3, #21cbf3);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.priority-section {
  position: relative;
}

.priority-section::before {
  content: '';
  position: absolute;
  left: -20px;
  top: 0;
  bottom: 0;
  width: 2px;
  background: linear-gradient(to bottom, #e9ecef, transparent);
}

/* Estilos para formularios */
.v-text-field, .v-textarea {
  margin-bottom: 8px;
}

.v-form {
  background: #fafafa;
  border-radius: 12px;
  padding: 16px;
}

/* Efectos de carga y transiciones suaves */
.cards-wrapper {
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.cards-wrapper:hover {
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

/* Mejoras en el modal */
.v-dialog > .v-card {
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
}

.v-tabs-items {
  background: white;
}

/* Estilos para estados vacíos */
.empty-section {
  border: 2px dashed #dee2e6;
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
}

.empty-section:hover {
  border-color: #adb5bd;
  background: linear-gradient(135deg, #e9ecef 0%, #f8f9fa 100%);
}
.customer-card {
  width: 320px;
  border-radius: 16px !important;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border-left: 4px solid transparent;
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
}

.customer-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.15) !important;
}

.customer-card.draggable-card {
  cursor: grab;
  border-left-color: #2196f3;
  box-shadow: 0 4px 12px rgba(33, 150, 243, 0.2) !important;
}

.customer-card.draggable-card:active {
  cursor: grabbing;
  transform: rotate(2deg) scale(1.02);
}

.gap-2 {
 gap: 8px;
}

/* Animaciones para el drag & drop */
.flip-list-move {
 transition: transform 0.5s ease;
}

.flip-list-enter-active,
.flip-list-leave-active {
 transition: all 0.5s ease;
}

.flip-list-enter {
 opacity: 0;
 transform: translateY(20px) scale(0.9);
}

.flip-list-leave-to {
 opacity: 0;
 transform: translateY(-20px) scale(0.9);
}

/* Efectos de hover mejorados */
.v-btn:hover {
 transform: translateY(-1px);
}

.v-card:hover {
 transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Responsive mejorado */
@media (max-width: 960px) {
 .customer-card {
   width: 100%;
   max-width: 400px;
 }
 
 .cards-container .d-flex {
   flex-direction: column;
   align-items: center;
 }

 .d-flex.justify-space-between.flex-wrap {
   flex-direction: column;
   gap: 12px;
 }

 .d-flex.gap-2.flex-wrap {
   justify-content: center;
 }
}

@media (max-width: 600px) {
 .pa-6 {
   padding: 16px !important;
 }
 
 .customer-card {
   margin: 4px !important;
 }
}

/* Mejoras visuales adicionales */
.v-chip {
 font-weight: 500;
}

.text-h4 {
 background: linear-gradient(135deg, #2196f3, #21cbf3);
 -webkit-background-clip: text;
 -webkit-text-fill-color: transparent;
 background-clip: text;
}

.priority-section {
 position: relative;
}

.priority-section::before {
 content: '';
 position: absolute;
 left: -20px;
 top: 0;
 bottom: 0;
 width: 2px;
 background: linear-gradient(to bottom, #e9ecef, transparent);
}

/* Estilos para formularios */
.v-text-field, .v-textarea {
 margin-bottom: 8px;
}

.v-form {
 background: #fafafa;
 border-radius: 12px;
 padding: 16px;
}

/* Efectos de carga y transiciones suaves */
.cards-wrapper {
 transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.cards-wrapper:hover {
 box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

/* Mejoras en el modal */
.v-dialog > .v-card {
 box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
}

.v-tabs-items {
 background: white;
}

/* Estilos para estados vacíos */
.empty-section {
 border: 2px dashed #dee2e6;
 background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
}

.empty-section:hover {
 border-color: #adb5bd;
 background: linear-gradient(135deg, #e9ecef 0%, #f8f9fa 100%);
}

/* Estilos específicos para el modal de campaña */
.customer-form {
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  border-radius: 16px;
  padding: 24px;
  border: 1px solid #e9ecef;
}

.customer-form .s-text-field,
.customer-form .s-select-field {
  margin-bottom: 20px;
}

/* Animaciones */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Mejoras en los iconos del modal de campaña */
.customer-form .v-icon {
  opacity: 0.8;
  transition: opacity 0.2s ease;
}

.customer-form .v-text-field:focus-within .v-icon,
.customer-form .v-select:focus-within .v-icon {
  opacity: 1;
  transform: scale(1.1);
}

/* Mejoras en botones del modal de campaña */
.customer-form .v-btn {
  border-radius: 12px;
  text-transform: none;
  font-weight: 500;
  letter-spacing: 0.5px;
}

.customer-form .v-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
}

/* Indicadores de validación mejorados */
.customer-form .v-input--is-focused {
  position: relative;
}

.customer-form .v-input--is-focused::after {
  content: '';
  position: absolute;
  bottom: -2px;
  left: 0;
  right: 0;
  height: 2px;
  background: linear-gradient(90deg, #2196f3, #9c27b0);
  border-radius: 1px;
}

/* Estilos para s-text y s-select-generic específicos */
.customer-form >>> .s-text .v-input {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
}

.customer-form >>> .s-text .v-input:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  transform: translateY(-1px);
}

.customer-form >>> .s-select .v-input {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
}

.customer-form >>> .s-select .v-input:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  transform: translateY(-1px);
}

/* Divider con gradiente en el modal de campaña */
.customer-form + .v-divider {
  background: linear-gradient(90deg, transparent, #2196f3, transparent);
  height: 2px;
  margin: 24px 0;
}

/* Estilos específicos para las acciones del modal de campaña */
.modal-card .v-card-actions {
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border-top: 1px solid #e9ecef;
}

/* Mejoras en la vista previa */
.preview-card .customer-info {
  position: relative;
}

.preview-card .customer-info::before {
  content: '';
  position: absolute;
  left: -16px;
  top: 0;
  bottom: 0;
  width: 4px;
  background: linear-gradient(135deg, #2196f3, #9c27b0);
  border-radius: 2px;
}

/* Responsive específico para el modal de campaña */
@media (max-width: 600px) {
  .customer-form {
    padding: 16px;
  }
  
  .modal-card {
    margin: 16px;
    max-width: calc(100vw - 32px) !important;
  }
}

/* Estilo para el preview de color */
.color-preview {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 2px solid white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  transition: all 0.3s ease;
  margin-right: 8px;
}

.color-preview:hover {
  transform: scale(1.1);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

/* Estilos para el color picker */
.color-picker-container {
  background: white;
  border-radius: 16px;
  padding: 16px;
  border: 1px solid #e9ecef;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

.color-picker-container >>> .v-color-picker {
  width: 100% !important;
  border-radius: 12px;
  overflow: hidden;
}

.color-picker-container >>> .v-color-picker__canvas {
  border-radius: 8px;
}

.color-picker-container >>> .v-color-picker__controls {
  padding: 12px 16px;
}

.color-picker-container >>> .v-color-picker__hue {
  border-radius: 8px;
  height: 12px !important;
}

.color-info {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 12px;
  border: 1px solid #e9ecef;
}

.color-preview-large {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 3px solid white;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  transition: all 0.3s ease;
  flex-shrink: 0;
}

.color-preview-large:hover {
  transform: scale(1.1);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.3);
}

.hex-input {
  max-width: 150px;
}

.hex-input >>> .v-input__control {
  min-height: 40px !important;
}

.hex-input >>> .v-text-field__details {
  display: none;
}

/* Estilos para el botón de editar */
.edit-btn {
  opacity: 0.7;
  transition: all 0.3s ease;
  background: rgba(255, 255, 255, 0.9) !important;
}

.edit-btn:hover {
  opacity: 1;
  transform: scale(1.1);
  background: rgba(255, 152, 0, 0.1) !important;
}

.edit-btn:hover .v-icon {
  color: #FF9800 !important;
}

.customer-card:hover .edit-btn {
  opacity: 1;
  background: rgba(255, 255, 255, 1) !important;
}

/* Mejoras en la sección de botones en las cards */
.customer-card .d-flex.align-center.gap-2 {
  transition: all 0.3s ease;
}

/* Estilos para el botón de eliminar */
.delete-btn {
  opacity: 0.7;
  transition: all 0.3s ease;
  background: rgba(255, 255, 255, 0.9) !important;
}

.delete-btn:hover {
  opacity: 1;
  transform: scale(1.1);
  background: rgba(244, 67, 54, 0.1) !important;
}

.delete-btn:hover .v-icon {
  color: #f44336 !important;
}

.customer-card:hover .delete-btn {
  opacity: 1;
  background: rgba(255, 255, 255, 1) !important;
}

/* Efectos de hover específicos para el estado de drag */
.customer-card.draggable-card .edit-btn,
.customer-card.draggable-card .delete-btn {
  opacity: 0.5;
  pointer-events: none;
}

.customer-card.draggable-card .edit-btn:hover,
.customer-card.draggable-card .delete-btn:hover {
  transform: none;
  background: rgba(255, 255, 255, 0.9) !important;
}

.modern-header { 
    background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%); 
    border-radius: 16px; 
    padding: 24px 32px; 
    margin-bottom: 24px; 
    border: 1px solid rgba(0, 0, 0, 0.05); 
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04); 
} 

.title-indicator { 
    width: 4px; 
    height: 48px; 
    background: linear-gradient(135deg, var(--v-primary-base), var(--v-secondary-base)); 
    border-radius: 2px; 
} 

.title-section h1 { 
    line-height: 1.2; 
    letter-spacing: -0.025em; 
} 

.action-controls { 
    gap: 12px !important; 
} 

.control-btn { 
    background: rgb(83, 83, 83, 0.5) !important; 
    border-radius: 12px !important; 
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1); 
    position: relative;
} 

.control-btn:hover { 
    background: rgb(83, 83, 83, 0.2) !important; 
    transform: translateY(-1px); 
} 

.control-btn.drag-active { 
    background: var(--v-primary-base) !important; 
    color: white !important; 
    box-shadow: 0 4px 12px rgba(var(--v-primary-base), 0.3); 
    animation: dragActivePulse 2s infinite;
} 

.control-btn.drag-active .v-icon {
    animation: dragIconRotate 0.5s ease-in-out;
}

.primary-action-btn { 
    border-radius: 12px !important; 
    font-weight: 500 !important; 
    padding: 0 24px !important; 
    box-shadow: 0 4px 16px rgba(var(--v-primary-base), 0.2) !important; 
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important; 
} 

.primary-action-btn:hover { 
    transform: translateY(-2px); 
    box-shadow: 0 6px 20px rgba(var(--v-primary-base), 0.3) !important; 
} 

.primary-action-btn .v-icon { 
    margin-right: 8px !important; 
} 

/* Animaciones */
@keyframes dragActivePulse {
    0%, 100% {
        box-shadow: 0 4px 12px rgba(var(--v-primary-base), 0.3);
    }
    50% {
        box-shadow: 0 4px 16px rgba(var(--v-primary-base), 0.4);
    }
}

@keyframes dragIconRotate {
    0% { transform: rotate(0deg) scale(1); }
    50% { transform: rotate(10deg) scale(1.1); }
    100% { transform: rotate(0deg) scale(1); }
}

/* Estilos para la sección de filtros */
.filter-section {
    background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);
    border-radius: 16px !important;
    border: 1px solid rgba(0, 0, 0, 0.05);
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
    transition: all 0.3s ease;
}

.filter-section:hover {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
    transform: translateY(-1px);
}

.filter-section .v-card-text {
    background: transparent;
}

/* Estilos específicos para los componentes de filtro */
.filter-section >>> .s-select .v-input,
.filter-section >>> .s-select-definition .v-input {
    background: white;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
    transition: all 0.3s ease;
    border: 1px solid #e9ecef;
}

.filter-section >>> .s-select .v-input:hover,
.filter-section >>> .s-select-definition .v-input:hover {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
    transform: translateY(-1px);
    border-color: #2196f3;
}

.filter-section >>> .s-select .v-input:focus-within,
.filter-section >>> .s-select-definition .v-input:focus-within {
    box-shadow: 0 4px 20px rgba(33, 150, 243, 0.15);
    border-color: #2196f3;
}

.filter-section >>> .s-select-campaign-area .v-input,
.filter-section >>> .s-select-campaign-area .v-input {
    background: white;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
    transition: all 0.3s ease;
    border: 1px solid #e9ecef;
}

.filter-section >>> .s-select-campaign-area .v-input:hover {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
    transform: translateY(-1px);
    border-color: #2196f3;
}

.filter-section >>> .s-select-campaign-area .v-input:focus-within {
    box-shadow: 0 4px 20px rgba(33, 150, 243, 0.15);
    border-color: #2196f3;
}

/* Estilo del botón limpiar filtros */
.filter-section .v-btn {
    border-radius: 12px;
    text-transform: none;
    font-weight: 500;
    transition: all 0.3s ease;
}

.filter-section .v-btn:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

/* Responsive */ 
@media (max-width: 768px) { 
    .modern-header { 
        padding: 16px 20px; 
    } 
    .d-flex.justify-space-between { 
        flex-direction: column; 
        align-items: flex-start !important; 
        gap: 16px; 
    } 
    .action-controls { 
        align-self: stretch; 
        justify-content: space-between; 
    } 
    .primary-action-btn { 
        flex: 1; 
        max-width: none; 
    }
    .filter-section {
        margin-bottom: 16px;
    }
}
</style>